import { type Directive } from "vue"
import {
  computePosition,
  autoPlacement,
  autoUpdate,
  offset,
  shift,
  type Placement,
} from "@floating-ui/dom"

type TooltipPosition = Placement | "auto"

type TooltipAnchor = HTMLElement & {
  _tooltip: Tooltip
}

type TooltipDirective = Directive<
  TooltipAnchor,
  string,
  string,
  TooltipPosition
>

interface Tooltip {
  el: HTMLElement
  hide: () => void
  setContent: (content: string) => void
  setPosition: (position: TooltipPosition) => void
  show: () => void
}

/**
 * newTooltip constructs the HTML markup neccessary for displaying a tooltip
 * and provides methods for showing, hiding and setting config values.
 *
 * Markup (without classes):
 * <div>
 *   <div>${content}</div>
 *   <div><div><!--tooltip arrow-->
 * </div>
 *
 * @returns Tooltip
 */
const newTooltip = (
  anchor: HTMLElement,
  content = "",
  position: TooltipPosition = "auto"
): Tooltip => {
  const tooltipAnchor = anchor
  let tooltipText = content
  let tooltipPosition = position

  // Auto positioning applies event handlers to dom elements that should be
  // removed any time tooltip element is removed from the dom.
  let autoPositionCleanup = null

  // Wrapper
  const tooltipWrapper = document.createElement("div")
  tooltipWrapper.className = "fixed top-0 left-0 z-10"

  // Content
  const tooltipContent = document.createElement("div")
  tooltipContent.className =
    "max-w-xs bg-xy-indigo rounded px-3 py-2 text-sm/6 text-white font-semibold relative drop-shadow-md"
  tooltipContent.innerText = tooltipText

  // Arrow
  const tooltipArrow = document.createElement("div")
  tooltipArrow.className = getArrowClasses()

  // Assemble
  tooltipWrapper.appendChild(tooltipContent)
  tooltipWrapper.appendChild(tooltipArrow)

  return {
    el: tooltipWrapper,
    hide: () => {
      autoPositionCleanup?.()

      if (document.body.contains(tooltipWrapper)) {
        document.body.removeChild(tooltipWrapper)
      }
    },
    setContent: (content: string) => {
      tooltipText = content
      tooltipContent.innerText = content
    },
    setPosition: (position: TooltipPosition) => {
      tooltipPosition = position
    },
    show: () => {
      if (!tooltipText) {
        return
      }

      if (!document.body.contains(tooltipWrapper)) {
        document.body.appendChild(tooltipWrapper)
      }

      autoPositionCleanup = autoUpdate(
        tooltipAnchor,
        tooltipWrapper,
        async () => {
          const { x, y, placement } = await computePosition(
            tooltipAnchor,
            tooltipWrapper,
            {
              placement:
                tooltipPosition === "auto" ? undefined : tooltipPosition,
              middleware: getMiddleware(tooltipPosition),
              strategy: "fixed",
            }
          )

          // NOTE(spk): do a full classname replacement on the arrow
          tooltipArrow.className = getArrowClasses(placement)

          Object.assign(tooltipWrapper.style, {
            left: `${x}px`,
            top: `${y}px`,
          })
        }
      )
    },
  }
}

/**
 * getMiddleware determines the set of middleware adapters to provide to floating/ui's
 * computePosition function.
 *
 * Note the placement arg is of type TooltipPosition and not Placement.
 *
 * @param placement TooltipPosition
 * @returns Middleware
 */
const getMiddleware = (placement?: TooltipPosition) => {
  const middleware = [offset(10), shift()]
  if (placement === "auto") {
    middleware.push(autoPlacement())
  }

  return middleware
}

/**
 * getArrowClasses computes all classes necessary to position an arrow
 * based on the Placement computed by floating/ui
 *
 * @param placement Placement
 * @returns string
 */
const getArrowClasses = (placement?: Placement) => {
  const baseClasses =
    "absolute bg-xy-indigo h-3.5 w-3.5 rounded-sm rotate-45 z-0"

  // prettier-ignore
  const positionClasses = {
    "top":
      "top-full -translate-y-1/2 left-1/2 -translate-x-1/2 -my-[1px]",
    "bottom":
      "bottom-full translate-y-1/2 left-1/2 -translate-x-1/2 -my-[1px]",
    "left":
      "top-1/2 -translate-y-1/2 left-full -translate-x-1/2 -mx-[1px]",
    "right":
      "top-1/2 -translate-y-1/2 right-full translate-x-1/2 -mx-[1px]",
    "top-start":
      "top-full -translate-y-1/2 left-0 translate-x-full -my-[1px]",
    "top-end":
      "top-full -translate-y-1/2 right-0 -translate-x-full -my-[1px]",
    "bottom-start":
      "bottom-full translate-y-1/2 left-0 translate-x-full -my-[1px]",
    "bottom-end":
      "bottom-full translate-y-1/2 right-0 -translate-x-full -my-[1px]",
    "left-start":
      "top-0.5 translate-y-1/2 left-full -translate-x-1/2 -mx-[1px]",
    "left-end":
      "bottom-0.5 -translate-y-1/2 left-full -translate-x-1/2 -mx-[1px]",
    "right-start":
      "top-0.5 translate-y-1/2 right-full translate-x-1/2 -mx-[1px]",
    "right-end":
      "bottom-0.5 -translate-y-1/2 right-full translate-x-1/2 -mx-[1px]",
  }

  return `${baseClasses} ${positionClasses[placement] || ""}`
}

/**
 * tooltipDirective is a vue.js directive for displaying simple tooltips
 * on dom elements and components.  The directive supports one argument for
 * manually positioning the tooltip and otherwise defaults to "auto" positioning.
 *
 * Note that the value provided to the directive is a JavaScript expression and not a plain string.
 *
 * example usage:
 *
 * Auto Positioned
 * <button v-tooltip="'This is static tooltip content'">Click Me!</button>
 *
 * Top Positioned
 * <button v-tooltip:top="'This is static tooltip content'">Click Me!</button>
 *
 * Dynamic Content and Positioning
 * const content = ref("My dynamic tooltip content")
 * const position = ref("bottom")
 *
 * <button v-tooltip:[position]="content">Click Me!</button>
 */
const tooltipDirective: TooltipDirective = {
  mounted: (el, binding) => {
    const tooltip = newTooltip(el, binding.value, binding.arg)
    el._tooltip = tooltip

    // Attach hover event listener to anchor
    el.addEventListener("mouseenter", tooltip.show)
    el.addEventListener("mouseleave", tooltip.hide)
  },

  updated(el, binding) {
    el._tooltip?.setContent(binding.value)
    el._tooltip?.setPosition(binding.arg)
  },

  beforeUnmount(el) {
    el._tooltip?.hide()

    // Remove event listeners on anchor
    el.removeEventListener("mouseenter", el._tooltip.show)
    el.removeEventListener("mouseleave", el._tooltip.hide)

    // Remove tooltip data
    delete el._tooltip
  },
}

export default tooltipDirective
